import { compact, find, join, map, sortBy, uniq } from 'es-toolkit/compat';

import { FeatureMetadata, ResultOverlaySection } from './featureMetadata';

export type CellSegmentationCompartment = 'membrane' | 'nucleus';

type VisualizationTypeOption = {
  id?: string;
  index?: number;
};

// These indexes are the seeded visualization rows used when the options catalog is not loaded.
// nuclei is index 6; whole_cell_segmentation is index 12 (V447).
const fallbackVisualizationIndexToCompartment: Record<string, CellSegmentationCompartment> = {
  '6': 'nucleus',
  '12': 'membrane',
};

const visualizationIndexPattern = /<v:(\d+)>/;

export const getVisualizationIndexFromKey = (key?: string) => `${key || ''}`.match(visualizationIndexPattern)?.[1];

export const getCellSegmentationCompartmentFromVisualizationId = (
  visualizationId?: string,
  visualizationTypeOptions: VisualizationTypeOption[] = []
): CellSegmentationCompartment | undefined => {
  if (!visualizationId) {
    return undefined;
  }
  const matchedOption = find(visualizationTypeOptions, (option) => `${option.index}` === visualizationId);
  if (matchedOption?.id === 'nuclei') {
    return 'nucleus';
  }
  if (matchedOption?.id === 'whole_cell_segmentation') {
    return 'membrane';
  }
  if (matchedOption) {
    return undefined;
  }
  return fallbackVisualizationIndexToCompartment[visualizationId];
};

// Presentation-info and option keys, not display names.
// Intermediate exports use pmt_* names; post-processing uses <v:N> visualization keys.
export const getCellSegmentationCompartmentFromResultKey = (key?: string): CellSegmentationCompartment | undefined => {
  const normalizedKey = `${key || ''}`.toLowerCase();
  if (!normalizedKey) {
    return undefined;
  }
  const visualizationCompartment = getCellSegmentationCompartmentFromVisualizationId(
    getVisualizationIndexFromKey(normalizedKey)
  );
  if (visualizationCompartment) {
    return visualizationCompartment;
  }
  if (normalizedKey.includes('nuclear_geometry') || normalizedKey.includes('nuclear')) {
    return 'nucleus';
  }
  if (normalizedKey.includes('whole_cell') || normalizedKey.includes('cell_geometry')) {
    return 'membrane';
  }
  return undefined;
};

// Option layer keys written by cell-segmentation export. A bare "cell" layer is a cell class, not membrane.
export const getCellSegmentationCompartmentFromLayerKey = (key?: string): CellSegmentationCompartment | undefined => {
  const normalizedKey = `${key || ''}`.trim().toLowerCase();
  if (normalizedKey === 'nuclear_geometry' || normalizedKey === 'nuclear' || normalizedKey === 'nuclei') {
    return 'nucleus';
  }
  if (normalizedKey === 'cell_geometry') {
    return 'membrane';
  }
  return undefined;
};

export const getCellSegmentationCompartmentLabel = (compartment: CellSegmentationCompartment, dilation?: number) => {
  const name = compartment === 'membrane' ? 'Membrane' : 'Nucleus';
  return Number.isFinite(dilation) ? `${name}, ${dilation} µm` : name;
};

export const getHeatmapCellSegmentationCompartment = (
  heatmap: Pick<FeatureMetadata, 'key' | 'visualizationId' | 'segmentationCompartment'>,
  visualizationTypeOptions: VisualizationTypeOption[] = []
): CellSegmentationCompartment | undefined => {
  if (heatmap.segmentationCompartment) {
    return heatmap.segmentationCompartment;
  }
  const visualizationId = heatmap.visualizationId || getVisualizationIndexFromKey(heatmap.key);
  return (
    getCellSegmentationCompartmentFromVisualizationId(visualizationId, visualizationTypeOptions) ||
    getCellSegmentationCompartmentFromResultKey(heatmap.key)
  );
};

export const getLayerCellSegmentationCompartment = (
  layerKey: string | undefined,
  heatmap: Pick<FeatureMetadata, 'key' | 'visualizationId' | 'segmentationCompartment'>
): CellSegmentationCompartment | undefined =>
  getCellSegmentationCompartmentFromLayerKey(layerKey) || getHeatmapCellSegmentationCompartment(heatmap);

export const resolveParsedCellSegmentation = ({
  isIntermediate,
  key,
  visualizationId,
  layerKeys,
  visualizationTypeOptions = [],
}: {
  isIntermediate: boolean;
  key?: string;
  visualizationId?: string;
  layerKeys: Array<string | number | undefined>;
  visualizationTypeOptions?: VisualizationTypeOption[];
}): {
  overlaySection?: ResultOverlaySection;
  segmentationCompartment?: CellSegmentationCompartment;
} => {
  const layerCompartments = uniq(
    compact(map(layerKeys, (layerKey) => getCellSegmentationCompartmentFromLayerKey(`${layerKey ?? ''}`)))
  );
  const segmentationCompartment =
    getCellSegmentationCompartmentFromVisualizationId(visualizationId, visualizationTypeOptions) ||
    getCellSegmentationCompartmentFromResultKey(key) ||
    (layerCompartments.length === 1 ? layerCompartments[0] : undefined);
  const isCellSegmentation = isIntermediate || Boolean(segmentationCompartment) || layerCompartments.length > 0;

  return {
    overlaySection: isCellSegmentation ? ResultOverlaySection.CellSegmentation : undefined,
    segmentationCompartment,
  };
};

export const getCellSegmentationOverlayIdentity = (
  heatmap: Pick<
    FeatureMetadata,
    'overlaySection' | 'segmentationDilation' | 'segmentationCompartment' | 'key' | 'visualizationId' | 'nestedItems'
  >
) => {
  if (heatmap.overlaySection !== ResultOverlaySection.CellSegmentation) {
    return undefined;
  }
  const heatmapCompartment = getHeatmapCellSegmentationCompartment(heatmap);
  const layerCompartments = uniq(
    compact(map(heatmap.nestedItems, (layer) => getCellSegmentationCompartmentFromLayerKey(layer.key)))
  );
  const compartment =
    heatmapCompartment || (layerCompartments.length ? join(sortBy(layerCompartments), '+') : 'unspecified');
  return `${heatmap.overlaySection}:${compartment}:${heatmap.segmentationDilation ?? 'undilated'}`;
};
